import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { APP_LINK_STATE } from '../../Home/navRoutes';
import { COLORS } from '../../IndividualPlan/tokens';
import { schoolComponentOf, schoolHref } from '../listKeys';
import type { PeSchoolDetail } from '../types';
import { countWithShare } from '../utils/drilldownFormat';
import { CountLink } from './CountLink';
import { CELL_SX, FIRST_CELL_SX, HEAD_ROW_SX, ROW_SX, TABLE_SX, schoolsOmittedNote } from './GradeTable';
import { SmallGroupMarker } from './SmallGroupMarker';

export type SchoolTotals = {
  count: number;
  newcomers: number;
  ltel: number;
  met: number;
};

export function districtSchoolTotals(
  district: { el_student_count: number; in_program_count?: number },
  schools: PeSchoolDetail[],
): SchoolTotals {
  return {
    count: district.el_student_count,
    newcomers: schools.reduce((n, s) => n + s.newcomers, 0),
    ltel: schools.reduce((n, s) => n + s.ltel, 0),
    met: schools.reduce((n, s) => n + s.met, 0),
  };
}

const KINDS = ['Elementary', 'Middle', 'High', 'Other'];

const GROUP_ROW_SX = {
  ...HEAD_ROW_SX,
  py: 1,
  letterSpacing: 1,
  textTransform: 'uppercase',
} as const;
const SUBTOTAL_SX = {
  ...ROW_SX,
  color: COLORS.muted,
  fontWeight: 700,
} as const;
const TOTAL_SX = {
  ...ROW_SX,
  fontWeight: 700,
  borderTop: `2px solid ${COLORS.ink}`,
} as const;

export function schoolFootnote(threshold: number): string {
  return `The info marker means a school with fewer than ${threshold} ELs: it stays in the table but doesn’t produce findings. A finding about a school with ${threshold} to ${3 * threshold} ELs says so, because one or two students can move its rates.`;
}

export function SchoolDrilldownTable({
  schools,
  districtTotals,
  threshold,
  schoolsOmitted,
}: {
  schools: PeSchoolDetail[];
  districtTotals: SchoolTotals;
  threshold: number;
  schoolsOmitted?: number;
}) {
  const groups = KINDS.map((kind) => ({
    kind,
    rows: schools.filter((s) => s.kind === kind),
  })).filter((g) => g.rows.length > 0);
  const anySmall = schools.some((s) => s.small || s.caveat);
  const omittedNote = schoolsOmittedNote(schoolsOmitted, schools.length, 'EL students');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box data-testid="school-drilldown-table" sx={TABLE_SX}>
        <Box sx={HEAD_ROW_SX}>
          <Box sx={FIRST_CELL_SX}>School</Box>
          <Box sx={{ ...CELL_SX, width: 120 }}>ELs</Box>
          <Box sx={CELL_SX}>Newcomers</Box>
          <Box sx={CELL_SX}>Long-term ELs</Box>
          <Box sx={CELL_SX}>Met exit criteria</Box>
        </Box>
        {groups.map(({ kind, rows }) => {
          const t = rows.reduce(
            (acc, s) => ({
              count: acc.count + s.count,
              newcomers: acc.newcomers + s.newcomers,
              ltel: acc.ltel + s.ltel,
              met: acc.met + s.met,
            }),
            { count: 0, newcomers: 0, ltel: 0, met: 0 },
          );
          return (
            <Box key={kind} sx={{ display: 'contents' }}>
              <Box sx={GROUP_ROW_SX}>
                <Box
                  sx={FIRST_CELL_SX}
                >{`${kind} · ${rows.length} school${rows.length === 1 ? '' : 's'}`}</Box>
              </Box>
              {rows.map((s) => (
                <Box key={s.key} data-testid="school-row" sx={ROW_SX}>
                  <Box sx={FIRST_CELL_SX}>
                    <Link
                      component={RouterLink}
                      to={schoolHref(schoolComponentOf(s.key))}
                      state={APP_LINK_STATE}
                      underline="hover"
                      sx={{ color: COLORS.link, fontWeight: 600 }}
                    >
                      {s.school}
                    </Link>
                  </Box>
                  <Box sx={{ ...CELL_SX, width: 120 }}>
                    <CountLink count={s.count} listKey={s.key} />
                    {s.small && <SmallGroupMarker threshold={threshold} />}
                  </Box>
                  <Box sx={CELL_SX}>
                    {s.newcomers > 0 ? (
                      <CountLink count={s.newcomers} listKey={`cohort:newcomer:${s.key}`}>
                        {countWithShare(s.newcomers, s.count)}
                      </CountLink>
                    ) : (
                      '0'
                    )}
                  </Box>
                  <Box sx={CELL_SX}>
                    {s.ltel > 0 ? (
                      <CountLink count={s.ltel} listKey={`cohort:ltel:${s.key}`}>
                        {countWithShare(s.ltel, s.count)}
                      </CountLink>
                    ) : (
                      '0'
                    )}
                  </Box>
                  <Box sx={CELL_SX}>
                    <CountLink count={s.met} listKey={`${s.key}:met`}>
                      {countWithShare(s.met, s.count)}
                    </CountLink>
                  </Box>
                </Box>
              ))}
              <Box sx={SUBTOTAL_SX}>
                <Box sx={FIRST_CELL_SX}>{`All ${kind.toLowerCase()}`}</Box>
                <Box sx={{ ...CELL_SX, width: 120 }}>{t.count}</Box>
                <Box sx={CELL_SX}>{countWithShare(t.newcomers, t.count)}</Box>
                <Box sx={CELL_SX}>{countWithShare(t.ltel, t.count)}</Box>
                <Box sx={CELL_SX}>{countWithShare(t.met, t.count)}</Box>
              </Box>
            </Box>
          );
        })}
        <Box sx={TOTAL_SX}>
          <Box sx={FIRST_CELL_SX}>{`District · ${schools.length} schools`}</Box>
          <Box sx={{ ...CELL_SX, width: 120 }}>{districtTotals.count}</Box>
          <Box sx={CELL_SX}>{countWithShare(districtTotals.newcomers, districtTotals.count)}</Box>
          <Box sx={CELL_SX}>{countWithShare(districtTotals.ltel, districtTotals.count)}</Box>
          <Box sx={CELL_SX}>{countWithShare(districtTotals.met, districtTotals.count)}</Box>
        </Box>
      </Box>
      {anySmall && (
        <Typography
          component="p"
          data-testid="school-small-group-footnote"
          sx={{ m: 0, fontSize: 12, fontWeight: 500, color: COLORS.muted }}
        >
          {schoolFootnote(threshold)}
        </Typography>
      )}
      {omittedNote && (
        <Typography component="p" data-testid="schools-omitted-note" sx={{ m: 0, fontSize: 12, fontWeight: 500, color: COLORS.muted }}>
          {omittedNote}
        </Typography>
      )}
    </Box>
  );
}
